import axios from 'axios';
import { CATALOG_BASE_URL } from '../constants';
import { downloadBlob, postForFile } from './batch3Api';

export { downloadBlob };

export type EtbPeriod = 'period1' | 'period2';

export interface EtbPeriods {
  period1: string | null;
  period2: string | null;
}

export interface EtbPreview {
  columns: string[];
  rows: (string | number | null)[][];
  totals: { p1_debit: number; p1_credit: number; p2_debit: number; p2_credit: number };
  balanced: boolean;
}

export interface EtbSplitGroup {
  code: string;
  description: string;
  pdf_value: number | null;
  qb_names: string[];
}

export interface EtbSummary {
  etb_rows: number;
  bs_rows: number;
  pl_rows: number;
  pdf_rows: number;
  dupe_codes: string[];
  copied_sheet_names: string[];
}

export interface EtbFiles {
  coa: File | null;
  mapping: File | null;
  qbCurrent: File | null;
  qbLast: File | null;
  clientLast: File | null;
  apPdf: File | null;
  extra: File | null;
}

async function postJson<T>(path: string, form: FormData, failure: string): Promise<T> {
  try {
    return (await axios.post<T>(`${CATALOG_BASE_URL}${path}`, form)).data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') throw new Error(detail);
      if (!error.response) throw new Error('Could not reach the backend. Is it running?');
      throw new Error(`${failure} (status ${error.response.status})`);
    }
    throw error;
  }
}

export function fetchEtbPeriods(apPdf: File) {
  const form = new FormData();
  form.append('ap_tb_pdf_file', apPdf);
  return postJson<EtbPeriods>('/etb-interlink/periods', form, 'Could not read the PDF periods');
}

export function previewEtbPdf(coa: File, apPdf: File) {
  const form = new FormData();
  form.append('coa_file', coa);
  form.append('ap_tb_pdf_file', apPdf);
  return postJson<EtbPreview>('/etb-interlink/preview', form, 'PDF extraction failed');
}

export async function fetchEtbSplitGroups(coa: File, mapping: File, apPdf: File, usePeriod: EtbPeriod) {
  const form = new FormData();
  form.append('coa_file', coa);
  form.append('mapping_file', mapping);
  form.append('ap_tb_pdf_file', apPdf);
  form.append('options', JSON.stringify({ use_period: usePeriod }));
  const data = await postJson<{ groups: EtbSplitGroup[] }>('/etb-interlink/split-groups', form, 'Could not check shared codes');
  return data.groups;
}

export function generateEtb(
  files: EtbFiles,
  usePeriod: EtbPeriod,
  overrides: { code: string; qb_name: string; value: number }[]
) {
  const form = new FormData();
  form.append('coa_file', files.coa as File);
  form.append('mapping_file', files.mapping as File);
  form.append('qb_current_file', files.qbCurrent as File);
  form.append('qb_last_file', files.qbLast as File);
  form.append('client_last_file', files.clientLast as File);
  form.append('ap_tb_pdf_file', files.apPdf as File);
  if (files.extra) form.append('extra_sheets_file', files.extra);
  form.append('options', JSON.stringify({ use_period: usePeriod, py_overrides: overrides }));
  return postForFile<EtbSummary>('/etb-interlink/generate', form, 'ETB Interlink.xlsx', 'Building the workbook failed');
}
